import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Paragraph, DialogOverlay, DialogOverlayActions } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useEvent } from '@fs/zion-frontend-friends'
import { PillButton } from '../buttons'

export default function AreYouSureOverlay({
  instructions,
  onSaveDraftClick,
  onSubmit,
  saveDraftButtonText,
  saveDraftInstructions,
  submitButtonText,
  ...overlay
}) {
  const [t] = useTranslation()
  // The dialog can stay mounted (and its buttons clickable) for the duration
  // of its close transition, so a fast second click can fire the same action
  // again before it disappears. Disable the action buttons after the first
  // click to prevent that.
  const [isProcessing, setIsProcessing] = useState(false)
  const onClick = useEvent((onCallback) => {
    setIsProcessing(true)
    onCallback?.()
    overlay.close()
  })
  // Some consumers (e.g. UpdateCreatorForm) keep this component mounted and
  // just flip `isOpen` to reopen the same dialog instance rather than
  // mounting a fresh one, so isProcessing has to be reset on reopen too -
  // it won't get a fresh initial value from an unmount/remount.
  const { isOpen } = overlay
  useEffect(() => {
    if (isOpen) setIsProcessing(false)
  }, [isOpen])
  return (
    <DialogOverlay
      title={t('are-you-sure-overlay.heading', 'Are you sure?')}
      footer={
        <DialogOverlayActions>
          {onSaveDraftClick && (
            <PillButton
              onClick={() => onClick(onSaveDraftClick)}
              emphasis="low"
              linkName="Are You Sure: Draft"
              disabled={isProcessing}
            >
              {saveDraftButtonText || t('save.as.draft', 'Save Draft')}
            </PillButton>
          )}
          {!onSaveDraftClick && (
            <PillButton onClick={() => onClick(overlay.close())} emphasis="low" linkName="Are You Sure: Cancel">
              {t('common-ui:cancel.action', 'Cancel')}
            </PillButton>
          )}
          <PillButton
            onClick={() => onClick(onSubmit)}
            data-testid="button-yes-submit"
            linkName="Are You Sure: Submit"
            disabled={isProcessing}
          >
            {submitButtonText || t('common-ui:submit.action', 'Submit')}
          </PillButton>
        </DialogOverlayActions>
      }
      {...overlay}
    >
      {instructions && <Paragraph size="sm">{instructions}</Paragraph>}
      {onSaveDraftClick && saveDraftInstructions && <Paragraph size="sm">{saveDraftInstructions}</Paragraph>}
    </DialogOverlay>
  )
}

AreYouSureOverlay.propTypes = {
  instructions: PropTypes.string,
  onSaveDraftClick: PropTypes.func,
  onSubmit: PropTypes.func,
  saveDraftButtonText: PropTypes.string,
  saveDraftInstructions: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  submitButtonText: PropTypes.string,
}
